import React from 'react';
import { Personnel } from '../../../../shared/types/game';

interface PersonnelCardProps {
  p: Personnel;
  compact: boolean;
  onOpenDetails: (p: Personnel) => void;
}

const PersonnelCard = React.memo<PersonnelCardProps>(({ p, compact, onOpenDetails }) => {
  if (compact) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 hover:border-blue-500 transition-colors aspect-square flex flex-col items-center justify-center relative group">
        {/* Badge actif/inactif */}
        <div className="absolute top-2 right-2">
          <span
            className={`px-1.5 py-0.5 rounded-full text-xs ${
              p.actif ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
            }`}
          >
            {p.actif ? '✓' : '○'}
          </span>
        </div>

        {/* Image du grade */}
        <div className="flex-shrink-0 mb-2">
          {p.grade_img ? (
            <img
              src={p.grade_img}
              alt={p.grade || 'Grade'}
              className="h-12 w-12 object-contain mx-auto"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className="h-12 w-12 bg-red-600 rounded flex items-center justify-center text-white font-bold text-sm mx-auto">
              ?
            </div>
          )}
        </div>

        <h4 className="text-sm font-semibold text-white text-center truncate w-full px-2 mb-1">
          {p.prenom} {p.nom}
        </h4>
        <span className="text-xs text-blue-400 text-center mb-1">{p.grade}</span>
        <span className="text-xs text-gray-400 text-center mb-2">{p.age} ans</span>

        {p.bonus && p.bonus.length > 0 && (
          <div className="mt-auto pt-2 border-t border-gray-700 w-full">
            <div className="text-center">
              <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                {p.bonus.length} bonus{p.bonus.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails(p);
          }}
          className="absolute top-2 left-2 text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
          title="Voir les détails"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {p.grade_img ? (
              <img
                src={p.grade_img}
                alt={p.grade || 'Grade'}
                className="h-8 w-8 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="h-8 w-8 bg-red-600 rounded flex items-center justify-center text-white font-bold text-xs">
                ?
              </div>
            )}
            <h4 className="text-lg font-semibold text-white">
              {p.prenom} {p.nom}
            </h4>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
              {p.grade}
            </span>
            <span
              className={`px-2 py-1 rounded text-xs ${
                p.actif ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
              }`}
            >
              {p.actif ? 'Actif' : 'Inactif'}
            </span>
            <span className="text-xs text-gray-400">{p.age} ans</span>
          </div>
          {p.bonus && p.bonus.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {p.bonus.map((bonus, idx) => (
                <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                  {bonus.type}: +{bonus.valeur}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails(p);
          }}
          className="ml-4 text-gray-400 hover:text-white transition-colors"
          title="Voir les détails"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
});

PersonnelCard.displayName = 'PersonnelCard';

export default PersonnelCard;
